import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Calendar, CheckCircle, User } from 'lucide-react';
import Select from 'react-select';
import CalendarPicker from './CalendarPicker.js';
import { fetchSystemConstantsAction } from '../../../actions/systemConstantsActions.js';
import { getStatusColor } from '../../appointment/appointmentFunctions.js';

export default function FilterDropdown({ onFilterChange }) {
    const dispatch = useDispatch();
    
    // Get system constants from Redux store FIRST
    const systemConstants = useSelector(state => state.systemConstants?.constants);
    const systemConstantsLoading = useSelector(state => state.systemConstants?.loading);
    const systemConstantsError = useSelector(state => state.systemConstants?.error);
    const loginDetails = useSelector(state => state.loginDetails);
    const practiceDetails = useSelector(state => state.practiceDetails);
    const practice = practiceDetails?.practice;
    const practiceUsers = practice?.user || [];
    
    const [isOpen, setIsOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState('date'); // Track which filter panel is active
    
    // Initialize with default date range (last 30 days)
    const getDefaultDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30); // 30 days ago
        
        return {
            start: startDate.toISOString().split('T')[0], // Format: YYYY-MM-DD
            end: endDate.toISOString().split('T')[0]
        };
    };
    
    const [dateRange, setDateRange] = useState(getDefaultDateRange());
    
    // Get status options from system constants or fallback to defaults
    const statusOptions = systemConstants?.statuses 
        ? Object.values(systemConstants.statuses)
        : ['NEW', 'ACTIVE', 'SUSPENDED', 'CLOSED', 'DECLINED']; // Fallback when system constants not loaded
    
    // Initialize with all statuses selected by default
    const [selectedStatuses, setSelectedStatuses] = useState(statusOptions);
    const [selectedAssociate, setSelectedAssociate] = useState(''); // Store user ID

    // Function to apply filters when Apply button is clicked
    const applyFilters = () => {
        console.log('=== APPLY FILTERS CLICKED ===');
        console.log('Date range:', dateRange);
        console.log('Selected statuses:', selectedStatuses);
        console.log('Selected associate:', selectedAssociate);
        
        const combinedFilter = {};
        let hasActiveFilters = false;
        
        // Add date filter if active
        if (dateRange.start && dateRange.end) {
            combinedFilter.date = {
                type: 'date',
                value: dateRange
            };
            hasActiveFilters = true;
        }
        
        // Add status filter if active
        if (selectedStatuses.length > 0) {
            combinedFilter.status = {
                type: 'status',
                value: selectedStatuses
            };
            hasActiveFilters = true;
        }
        
        // Add associate filter if active
        if (selectedAssociate.trim()) {
            combinedFilter.associate = {
                type: 'associate',
                value: selectedAssociate // This is now the user ID
            };
            hasActiveFilters = true;
        }
        
        // Send the combined filter object containing ALL active filters
        const filterToSend = hasActiveFilters ? {
            type: 'combined',
            value: combinedFilter
        } : null;
        
        console.log('Final filter to send:', filterToSend);
        onFilterChange(filterToSend);
        setIsOpen(false); // Close dropdown after applying filters
    };

    const clearFilters = () => {
        setDateRange({ start: '', end: '' });
        setSelectedStatuses([]);
        setSelectedAssociate('');
        setActiveFilter(null);
        onFilterChange(null);
    };

    // Apply default date and status filter on component mount only
    useEffect(() => {
        console.log('=== FILTER DROPDOWN MOUNT - APPLYING DEFAULT FILTERS ===');
        console.log('Default date range:', dateRange);
        
        // Apply the default date and status filter on mount
        const defaultFilter = {
            type: 'combined',
            value: {
                date: {
                    type: 'date',
                    value: dateRange
                },
                status: {
                    type: 'status',
                    value: statusOptions // Include all statuses by default
                }
            }
        };
        
        console.log('Applying default filter:', defaultFilter);
        onFilterChange(defaultFilter);
    }, []); // Only run on mount

    // Fetch system constants if not loaded and user is logged in
    useEffect(() => {
        
        if (!systemConstants && !systemConstantsLoading && !systemConstantsError && loginDetails?.jwtToken) {
            console.log('Fetching system constants...');
            fetchSystemConstantsAction(loginDetails.jwtToken)(dispatch)
                .then(() => {
                    console.log('System constants fetch completed');
                })
                .catch((error) => {
                    console.error('Failed to fetch system constants:', error);
                });
        } else {
            console.log('Not fetching system constants - conditions not met');
        }
    }, [systemConstants, systemConstantsLoading, systemConstantsError, loginDetails?.jwtToken, dispatch]);

    const filterOptions = [
        { id: 'date', label: 'Date', icon: Calendar },
        { id: 'status', label: 'Status', icon: CheckCircle },
        { id: 'associate', label: 'Associate Name', icon: User }
    ];

    
    const handleStatusChange = (status) => {
        // Toggle status: remove if already present, add if not present
        setSelectedStatuses(prevSelectedStatuses => {
            console.log('=== STATUS TOGGLE DEBUG ===');
            console.log('Previous selectedStatuses:', prevSelectedStatuses);
            console.log('Toggling status:', status);
            
            const statusIndex = prevSelectedStatuses.indexOf(status);
            let newSelectedStatuses;
            
            if (statusIndex > -1) {
                // Status already selected, remove it
                newSelectedStatuses = prevSelectedStatuses.filter((s, index) => index !== statusIndex);
                console.log(`Removed status: "${status}"`);
            } else {
                // Status not selected, add it
                newSelectedStatuses = [...prevSelectedStatuses, status];
                console.log(`Added status: "${status}"`);
            }
            
            console.log('New selectedStatuses:', newSelectedStatuses);
            console.log('=== END STATUS TOGGLE DEBUG ===');
            
            // No immediate filter application - wait for Apply button
            
            return newSelectedStatuses;
        });
    };

    const handleAssociateChange = (selectedOption) => {
        setSelectedAssociate(selectedOption ? selectedOption.value : '');
        // No immediate filter application - wait for Apply button
    };

    // Helper function to get user display name from ID
    const getUserName = (userId) => {
        const user = practiceUsers.find(u => u._id === userId);
        return user ? `${user.firstname} ${user.lastname}` : '';
    };

    // Prepare options for react-select
    const associateOptions = practiceUsers
        .filter(user => user.firstname && user.lastname && user.firstname.trim() !== '' && user.lastname.trim() !== '')
        .map((user) => ({
            value: user._id,
            label: `${user.firstname} ${user.lastname}`
        }));

    const handleFilterSelect = (filterId) => {
        setActiveFilter(filterId);
    };

    return (
        <div className="relative">
            {/* Main Filter Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    (dateRange.start && dateRange.end) || selectedStatuses.length > 0 || selectedAssociate
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-300 hover:bg-gray-50'
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-sm font-medium">Filter</span>
                {(dateRange.start && dateRange.end) || selectedStatuses.length > 0 || selectedAssociate ? (
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                ) : null}
                <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="flex h-[400px]">
                        {/* Left Panel - Filter Options */}
                        <div className="w-1/3 border-r border-gray-200">
                            <div className="p-3">
                                <h3 className="text-sm font-semibold text-gray-900 mb-3">Filter by</h3>
                                <div className="space-y-1">
                                    {filterOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => handleFilterSelect(option.id)}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                                                activeFilter === option.id
                                                    ? 'bg-blue-50 text-blue-600'
                                                    : 'hover:bg-gray-100 text-gray-700'
                                            }`}
                                        >
                                            <option.icon className="w-4 h-4" />
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Panel - Filter Controls */}
                        <div className="w-2/3 p-4">
                            {activeFilter === 'date' && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Date Range</h3>
                                    <CalendarPicker
                                        initialRange={dateRange}
                                        onDateRangeChange={(range) => {
                                            setDateRange(range);
                                            // No immediate filter application - wait for Apply button
                                        }}
                                    />
                                </div>
                            )}

                            {activeFilter === 'status' && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Status</h3>
                                    <div className="space-y-2 max-h-80 overflow-y-auto">
                                        {statusOptions.map((status) => (
                                            <label
                                                key={status}
                                                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStatuses.includes(status)}
                                                    onChange={() => handleStatusChange(status)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                                    {status}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeFilter === 'associate' && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Associate Name</h3>
                                    <Select
                                        value={associateOptions.find(option => option.value === selectedAssociate) || null}
                                        onChange={handleAssociateChange}
                                        options={associateOptions}
                                        placeholder="Select an associate..."
                                        isClearable
                                        className="w-full"
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                                                '&:hover': {
                                                    borderColor: '#9ca3af',
                                                },
                                                boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
                                                borderRadius: '0.375rem',
                                                minHeight: '40px',
                                            }),
                                            option: (baseStyles) => ({
                                                ...baseStyles,
                                                padding: '8px 12px',
                                            }),
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Filter Summary */}
                    <div className="border-t border-gray-200 p-3">
                        <div className="mb-2">
                            <h4 className="text-xs font-medium text-gray-700 mb-2">Selected Filters:</h4>
                            <div className="flex flex-wrap gap-1">
                                {/* Date Range Badge */}
                                {dateRange.start && dateRange.end && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                        📅 {dateRange.start} to {dateRange.end}
                                    </span>
                                )}
                                
                                {/* Status Badges */}
                                {selectedStatuses.map((status) => (
                                    <span
                                        key={status}
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}
                                    >
                                        {status}
                                    </span>
                                ))}
                                
                                {/* Associate Badge */}
                                {selectedAssociate.trim() && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                        👤 {getUserName(selectedAssociate)}
                                    </span>
                                )}
                                
                                {/* No filters message */}
                                {(!dateRange.start || !dateRange.end) && selectedStatuses.length === 0 && !selectedAssociate.trim() && (
                                    <span className="text-xs text-gray-500 italic">No filters selected</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 p-3 flex justify-between items-center">
                        <button
                            onClick={clearFilters}
                            className="text-sm text-gray-600 hover:text-gray-800"
                        >
                            Clear Filters
                        </button>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={applyFilters}
                                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Click outside to close */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
}
